import { useState, useEffect } from 'react';
import { onAuthStateChange } from '../firebase/firebaseAuth';

export const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((firebaseUser: any) => {
      if (firebaseUser) {
        // Afficher l'UI immédiatement avec un user minimal (pas d'attente Firestore)
        const fallbackName = firebaseUser.email?.split('@')[0] || 'Utilisateur';
        setUser({
          email: firebaseUser.email,
          uid: firebaseUser.uid,
          displayName: fallbackName,
          name: fallbackName,
          firstName: '',
          lastName: '',
          prenom: '',
          nom: '',
          username: fallbackName,
          role: 'Client',
          companyName: null
        });
        setLoading(false);

        // Charger le profil Firestore en arrière-plan puis mettre à jour l'utilisateur (ou déconnecter si inactif)
        (async () => {
          try {
            const { getUserProfile, getProviderProfile } = await import('../firebase/firebaseFirestore');
            let result = await getUserProfile(firebaseUser.email);
            let isProvider = false;
            if (!result.success || !result.data) {
              result = await getProviderProfile(firebaseUser.email);
              isProvider = true;
            }
            if (result.success && result.data) {
              if (result.data.status === 'inactive') {
                const { logout: firebaseLogout } = await import('../firebase/firebaseAuth');
                await firebaseLogout();
                setUser(null);
                return;
              }
              const firstName = result.data.firstName || result.data.prenom || '';
              const lastName = result.data.lastName || result.data.nom || '';
              const fullName = firstName && lastName
                ? `${firstName} ${lastName}`
                : firstName || lastName || result.data.name || fallbackName;
              let displayName;
              if (isProvider || result.data.role === 'Prestataire') {
                displayName = result.data.companyName || result.data.name || result.data.username || fullName;
              } else {
                displayName = result.data.name || result.data.username || fullName;
              }
              setUser({
                email: firebaseUser.email,
                uid: firebaseUser.uid,
                displayName,
                name: result.data.name || fullName,
                firstName,
                lastName,
                prenom: firstName,
                nom: lastName,
                username: result.data.username || result.data.name || displayName,
                role: result.data.role || (isProvider ? 'Prestataire' : 'Client'),
                companyName: result.data.companyName || null
              });
            } else {
              const { logout: firebaseLogout } = await import('../firebase/firebaseAuth');
              await firebaseLogout();
              setUser(null);
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Erreur chargement profil Firestore:', error);
            const { logout: firebaseLogout } = await import('../firebase/firebaseAuth');
            await firebaseLogout();
            setUser(null);
          }
        })();
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const login = (userData: any) => setUser(userData);

  const logout = async () => {
    const { logout: firebaseLogout } = await import('../firebase/firebaseAuth');
    await firebaseLogout();
    setUser(null);
  };

  return { user, loading, login, logout };
};